import './ProviderSearch.css';

import React, { useEffect, useRef, useState } from 'react';

import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface IsReferableIssue {
  NPI: string;
  FullName: string;
  IsReferable: boolean | null;
  IsGME: boolean;
  Slots: number;
  Specialty: string | null;
  EpicSpecialty: string | null;
  IsOpenSchedulingAvailable: boolean | null;
  photoUrl: string | null;
  LastImportDate: string | null;
}

type SortField = 'NPI' | 'FullName' | 'EpicSpecialty' | 'IsOpenSchedulingAvailable' | 'IsReferable' | 'IsGME' | 'Slots' | 'LastImportDate';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 50;

const VIEW_STATE_KEY = 'providerValidator.auditIsReferable.viewState';

interface AuditViewState {
  searchTerm: string;
  showOnlyWithSlots: boolean;
  excludeGME: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  currentPage: number;
}

const SCROLL_STATE_KEY = 'providerValidator.auditIsReferable.scrollY';

const loadViewState = (): Partial<AuditViewState> => {
  try {
    return JSON.parse(sessionStorage.getItem(VIEW_STATE_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveViewState = (state: AuditViewState): void => {
  try {
    sessionStorage.setItem(VIEW_STATE_KEY, JSON.stringify(state));
  } catch {
    /* ignore storage write failures */
  }
};

const loadScrollY = (): number => {
  const raw = sessionStorage.getItem(SCROLL_STATE_KEY);
  const y = raw ? Number(raw) : 0;
  return Number.isFinite(y) ? y : 0;
};

const saveScrollY = (y: number): void => {
  try {
    sessionStorage.setItem(SCROLL_STATE_KEY, String(y));
  } catch {
    /* ignore storage write failures */
  }
};

const AuditIsReferable: React.FC = () => {
  const savedView = useRef(loadViewState()).current;
  const [searchTerm, setSearchTerm] = useState(savedView.searchTerm ?? '');
  const [providers, setProviders] = useState<IsReferableIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showOnlyWithSlots, setShowOnlyWithSlots] = useState(savedView.showOnlyWithSlots ?? true);
  const [excludeGME, setExcludeGME] = useState(savedView.excludeGME ?? false);
  const [sortField, setSortField] = useState<SortField>(savedView.sortField ?? 'Slots');
  const [sortDirection, setSortDirection] = useState<SortDirection>(savedView.sortDirection ?? 'desc');
  const [currentPage, setCurrentPage] = useState(savedView.currentPage ?? 1);
  const requestAbortRef = useRef<AbortController | null>(null);
  const pendingScrollRef = useRef<number | null>(loadScrollY());
  const navigate = useNavigate();

  // Restore the previous view by reloading the data that matches the saved search
  useEffect(() => {
    runSearch(true);
    return () => {
      saveScrollY(window.scrollY);
      requestAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filter, sort, and pagination selections so returning restores the same view
  useEffect(() => {
    saveViewState({ searchTerm, showOnlyWithSlots, excludeGME, sortField, sortDirection, currentPage });
  }, [searchTerm, showOnlyWithSlots, excludeGME, sortField, sortDirection, currentPage]);

  // Restore the saved scroll position once the restored list has rendered
  useEffect(() => {
    if (pendingScrollRef.current !== null && !loading && providers.length > 0) {
      const y = pendingScrollRef.current;
      pendingScrollRef.current = null;
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [loading, providers]);

  const loadAllProviders = async (preservePage = false) => {
    requestAbortRef.current?.abort();
    const abortController = new AbortController();
    requestAbortRef.current = abortController;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/providers/audit/isreferable', {
        signal: abortController.signal
      });
      if (abortController.signal.aborted) return;
      setProviders(response.data);
      setInitialLoadDone(true);
      if (!preservePage) setCurrentPage(1);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      console.error('Error loading IsReferable audit data:', err);
      setError(err.response?.data?.message || 'Failed to load audit data');
      setProviders([]);
    } finally {
      if (requestAbortRef.current === abortController) {
        requestAbortRef.current = null;
        if (!abortController.signal.aborted) setLoading(false);
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch();
  };

  const runSearch = async (preservePage = false) => {
    if (!searchTerm.trim()) {
      loadAllProviders(preservePage);
      return;
    }

    setLoading(true);
    setError(null);

    requestAbortRef.current?.abort();
    const abortController = new AbortController();
    requestAbortRef.current = abortController;

    try {
      const response = await axios.get('/api/providers/audit/isreferable', {
        params: { search: searchTerm },
        signal: abortController.signal
      });
      if (abortController.signal.aborted) return;
      setProviders(response.data);
      if (!preservePage) setCurrentPage(1);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      console.error('Error searching IsReferable audit data:', err);
      setError(err.response?.data?.message || 'Failed to search audit data');
      setProviders([]);
    } finally {
      if (requestAbortRef.current === abortController) {
        requestAbortRef.current = null;
        if (!abortController.signal.aborted) setLoading(false);
      }
    }
  };

  const handleViewDetail = (npi: string) => {
    saveScrollY(window.scrollY);
    navigate(`/providers/${npi}`);
  };

  const handleSort = (field: SortField) => {
    setCurrentPage(1);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'Slots' ? 'desc' : 'asc');
    }
  };

  const getSortedProviders = () => {
    const filtered = providers.filter(p =>
      (!showOnlyWithSlots || p.Slots > 0) && (!excludeGME || !p.IsGME)
    );

    return [...filtered].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null values
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;

      // For EpicSpecialty, fallback to Specialty if null
      if (sortField === 'EpicSpecialty') {
        aVal = a.EpicSpecialty || a.Specialty || '';
        bVal = b.EpicSpecialty || b.Specialty || '';
      }

      // Compare values
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // For booleans
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        const aNum = aVal ? 1 : 0;
        const bNum = bVal ? 1 : 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      return 0;
    });
  };

  const getBooleanDisplay = (value: boolean | null, trueLabel: string = 'Yes', falseLabel: string = 'No') => {
    if (value === null) return 'N/A';
    return value ? trueLabel : falseLabel;
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedProviders = getSortedProviders();
  const totalPages = Math.max(1, Math.ceil(sortedProviders.length / PAGE_SIZE));
  const visibleProviders = sortedProviders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Keep the restored page within range if the filtered data set shrank. Gated on
  // the first load: before it, providers is empty and totalPages is 1, so this
  // would clamp a restored page back to 1 before the rows exist.
  useEffect(() => {
    if (initialLoadDone && currentPage > totalPages) setCurrentPage(totalPages);
  }, [initialLoadDone, currentPage, totalPages]);

  return (
    <div className="provider-search-container">
      <div className="intro-section">
        <h1>
          Audit IsReferable
          <span className="info-tooltip">
            <span className="info-icon">ⓘ</span>
            <span className="tooltip-text">
              This tool is based on an offline replica of the data. Any corrections in the source system will not be reflected here immediately.
              <br/><br/>
              Use the Provider Search tool to see real-time results.
            </span>
          </span>
        </h1>
        <p className="subtitle">
          Providers with IsReferable discrepancies (Is NOT Referable but has slots or is enabled for Open Scheduling).

        </p>
      </div>

      <div className="search-filters">
        <form onSubmit={handleSearch}>
          <div className="filter-row">
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Provider name or NPI..."
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
            </div>
            <div className="filter-group">
              <label>
                <input
                  type="checkbox"
                  checked={showOnlyWithSlots}
                  onChange={(e) => {
                    setShowOnlyWithSlots(e.target.checked);
                    setCurrentPage(1);
                  }}
                  style={{ marginRight: '8px' }}
                />
                Only show providers with slots
              </label>
            </div>
            <div className="filter-group">
              <label>
                <input
                  type="checkbox"
                  checked={excludeGME}
                  onChange={(e) => {
                    setExcludeGME(e.target.checked);
                    setCurrentPage(1);
                  }}
                  style={{ marginRight: '8px' }}
                />
                Exclude GME
              </label>
            </div>
          </div>

          <div className="filter-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  loadAllProviders();
                }}
                className="btn btn-secondary"
              >
                Clear Filters
              </button>
            )}
          </div>
        </form>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {!loading && initialLoadDone && providers.length === 0 && (
        <div className="no-results">
          {searchTerm ? `No providers found matching "${searchTerm}"` : 'No IsReferable issues found'}
        </div>
      )}

      {!loading && providers.length > 0 && sortedProviders.length === 0 && (
        <div className="no-results">
          No providers found for the selected filters.
        </div>
      )}

      {sortedProviders.length > 0 && (
        <div className="results-section">
          <div className="results-header">
            <p className="results-count">
              Found {sortedProviders.length} provider{sortedProviders.length !== 1 ? 's' : ''}
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </p>
          </div>

          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('NPI')}>
                    NPI{getSortIcon('NPI')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('FullName')}>
                    Name{getSortIcon('FullName')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('EpicSpecialty')}>
                    Epic Specialty{getSortIcon('EpicSpecialty')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('IsOpenSchedulingAvailable')}>
                    Open Sch Enabled{getSortIcon('IsOpenSchedulingAvailable')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('IsReferable')}>
                    Is Referable{getSortIcon('IsReferable')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('IsGME')}>
                    Is GME{getSortIcon('IsGME')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('Slots')}>
                    Slots{getSortIcon('Slots')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('LastImportDate')}>
                    Last Imported{getSortIcon('LastImportDate')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleProviders.map((provider, index) => (
                  <tr key={`${provider.NPI}-${index}`}>
                    <td>{provider.NPI}</td>
                    <td>
                      <div className="provider-name">
                        <div className="provider-thumbnail-wrapper">
                          {provider.photoUrl ? (
                            <img
                              src={provider.photoUrl}
                              alt={provider.FullName}
                              className="provider-thumbnail"
                            />
                          ) : (
                            <div className="provider-thumbnail-placeholder">
                              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <strong>{provider.FullName}</strong>
                      </div>
                    </td>
                    <td>{provider.EpicSpecialty || provider.Specialty || 'N/A'}</td>
                    <td>{getBooleanDisplay(provider.IsOpenSchedulingAvailable)}</td>
                    <td>{getBooleanDisplay(provider.IsReferable)}</td>
                    <td>{getBooleanDisplay(provider.IsGME)}</td>
                    <td><span className="badge">{provider.Slots}</span></td>
                    <td>{provider.LastImportDate ? new Date(provider.LastImportDate).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <button
                        onClick={() => handleViewDetail(provider.NPI)}
                        className="btn btn-sm btn-primary"
                      >
                        View Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
              >
                Previous
              </button>
              <span className="page-info">Page {currentPage} of {totalPages}</span>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditIsReferable;
